import React, { useState, useRef } from 'react';
import { Modal } from './ui/Modal';
import { Input, Label, Button } from './ui';
import { toast } from 'react-toastify';

// Modal (solo admin) para crear una notificación hacia los usuarios.
// Campos: título (obligatorio), descripción, imagen (opcional, URL).
// Destino: 'todos' (todos los no-admin) o roles específicos (contable/seguimiento/legal).
const ROLES_DISPONIBLES = [
    { value: 'contable', label: 'Contable' },
    { value: 'seguimiento', label: 'Seguimiento' },
    { value: 'legal', label: 'Legal' },
];

export function FormularioCrearNotificacion({ onSuccess, onCancel }) {
    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [imagen, setImagen] = useState('');
    const [target, setTarget] = useState('todos');
    const [roles, setRoles] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const fileInputRef = useRef(null);

    const getToken = () => {
        try {
            const raw = localStorage.getItem('token');
            return raw && raw !== '' ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    };

    const toggleRol = (value) => {
        setRoles(prev => prev.includes(value) ? prev.filter(r => r !== value) : [...prev, value]);
    };

    const handleImagenChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Si es una imagen, la convertimos a base64 para subirla (solo preview en este MVP).
        const reader = new FileReader();
        reader.onload = () => setImagen(reader.result);
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!titulo.trim()) {
            toast.warn('El título es obligatorio');
            return;
        }

        setEnviando(true);
        try {
            const res = await fetch('/api/notificaciones-usuario', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                },
                body: JSON.stringify({
                    titulo,
                    descripcion,
                    imagen: imagen || null,
                    target,
                    roles: target === 'roles' ? roles : []
                })
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data?.message || 'Error al crear la notificación');
            }
            toast.success(`Notificación creada para ${data?.data?.destinatarios || 0} usuario(s)`);
            onSuccess?.();
        } catch (error) {
            console.error('Error al crear notificación:', error);
            toast.error(error?.message || 'Error al crear la notificación');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <h3 className="text-lg font-semibold dark:text-gray-200">Nueva notificación</h3>

            {/* Título */}
            <div>
                <Label htmlFor="notif-titulo">Título *</Label>
                <Input
                    id="notif-titulo"
                    type="text"
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    placeholder="Ej.: Renovación de contraseñas"
                    required
                />
            </div>

            {/* Descripción */}
            <div>
                <Label htmlFor="notif-desc">Descripción</Label>
                <textarea
                    id="notif-desc"
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    placeholder="Detalle de la notificación"
                    rows={3}
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                />
            </div>

            {/* Imagen opcional */}
            <div>
                <Label>Imagen (opcional)</Label>
                <div className="flex items-center gap-3">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImagenChange}
                        className="hidden"
                    />
                    <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="!w-auto px-4 !bg-gray-200 dark:!bg-gray-700 !text-gray-800 dark:!text-gray-100 hover:!bg-gray-300"
                    >
                        Elegir imagen
                    </Button>
                    {imagen && (
                        <button
                            type="button"
                            onClick={() => setImagen('')}
                            className="text-sm text-red-600 hover:underline dark:text-red-400"
                        >
                            Quitar
                        </button>
                    )}
                </div>
                {imagen && (
                    <img src={imagen} alt="Vista previa" className="mt-2 max-h-40 rounded-md border border-gray-200 dark:border-gray-700" />
                )}
            </div>

            {/* Destino */}
            <div>
                <Label>Enviar a</Label>
                <div className="flex gap-4 mt-1">
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                            type="radio"
                            name="target"
                            checked={target === 'todos'}
                            onChange={() => setTarget('todos')}
                        />
                        Todos los usuarios (no admins)
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                            type="radio"
                            name="target"
                            checked={target === 'roles'}
                            onChange={() => setTarget('roles')}
                        />
                        Solo roles específicos
                    </label>
                </div>

                {target === 'roles' && (
                    <div className="flex gap-4 mt-2">
                        {ROLES_DISPONIBLES.map(r => (
                            <label key={r.value} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={roles.includes(r.value)}
                                    onChange={() => toggleRol(r.value)}
                                />
                                {r.label}
                            </label>
                        ))}
                    </div>
                )}
            </div>

            {/* Fecha de creación (solo lectura, se setea en backend) */}
            <p className="text-xs text-gray-400 dark:text-gray-500">
                Fecha de creación: se asigna automáticamente al guardar.
            </p>

            {/* Acciones */}
            <div className="flex justify-end gap-3 pt-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-md transition-colors"
                >
                    Cancelar
                </button>
                <Button type="submit" disabled={enviando}>
                    {enviando ? 'Creando...' : 'Crear notificación'}
                </Button>
            </div>
        </form>
    );
}
